import { isAllowedInCountry, type PageGateId } from "@/lib/country-gates"
import type { Country } from "@/i18n/config"
import { healthToolsKeys as k } from "@/i18n/health-tools-keys"

/**
 * The free calculator tools under `/tools/*`, and the one place a new one gets
 * registered.
 *
 * Every tool page renders `<OtherToolsSection>`, which lists this registry
 * minus itself — so adding a calculator is one entry here and it appears on
 * every existing tool page at once, which is the internal-linking payoff.
 *
 * Two rules keep that from ever producing a dead link:
 *
 *   - A tool is listed only if its `page:` gate allows the current country.
 *     The gate is the same one the route's `generateStaticParams` reads, so a
 *     tool that does not exist in a market cannot be linked from it.
 *   - An entry may only name a gate that exists in `COUNTRY_GATES`, which
 *     TypeScript enforces via `PageGateId`. A tool whose route has not been
 *     registered yet therefore cannot be added here at all.
 *
 * Locale is encoded too, in each entry's `locales`, because the tool pages no
 * longer all serve the same set: a link between them is only inside the same
 * locale if both ends have been translated into it. `healthToolsFor` filters
 * on both, so a card can never point at a locale its target does not
 * prerender. Keep an entry's `locales` in step with the `LOCALES` constant in
 * the route's own `page.tsx`, exactly as the sitemap entries are.
 */
/**
 * `MessageKeys` (global.d.ts) rather than `string`: a bare `string` widens out
 * of the catalog's key union and next-intl's typed `t()` rejects it at the
 * call site. None of these keys takes an ICU placeholder, so the union stays
 * callable with one argument.
 */
type HealthToolsKey = MessageKeys<"healthTools">

export interface HealthTool {
  /** Stable id, used to exclude the current page from its own list. */
  id: string
  /** Bare path, locale-stripped, for `useLocalePath()`. */
  path: string
  gate: PageGateId
  /**
   * The locales this tool's route actually prerenders, mirroring the `LOCALES`
   * constant in its own `page.tsx`. The gate answers "which countries" and
   * there is no locale-level gate registry, so this is the only place a
   * listing surface can learn that a tool exists in `en` but not yet in `de`.
   *
   * Without it a French tools index would link to `/ch/fr/tools/bmi-calculator`,
   * which `dynamicParams = false` makes a hard 404. Keep it in step with the
   * route: widening one without the other is the drift this field exists to
   * prevent.
   */
  locales: readonly string[]
  titleKey: HealthToolsKey
  descKey: HealthToolsKey
}

/**
 * Only the tools whose route exists on this branch.
 *
 * Order is the render order of the cards.
 */
export const HEALTH_TOOLS: readonly HealthTool[] = [
  {
    // CH + DE + NL, and the first of the six to reach a third market. Its
    // `locales` gains `nl` alongside the existing three: the gate says the
    // market has the page, this says which languages it was translated into,
    // and NL reads both (Dutch from messages/nl-NL.json, English from
    // messages/en-NL.json).
    id: "bmi-calculator",
    path: "/tools/bmi-calculator",
    gate: "page:tools/bmi-calculator",
    locales: ["de", "en", "fr", "nl"],
    titleKey: k.bmiTitle,
    descKey: k.bmiDesc,
  },
  {
    // CH + DE + NL, alongside the BMI card: neither Mifflin-St Jeor nor
    // Harris-Benedict carries a country term, so the Dutch rollout was the
    // copy in messages/nl-NL.json and messages/en-NL.json, and `locales` gains
    // `nl` for the language half of the question.
    id: "bmr-calculator",
    path: "/tools/bmr-calculator",
    gate: "page:tools/bmr-calculator",
    locales: ["de", "en", "fr", "nl"],
    titleKey: k.bmrTitle,
    descKey: k.bmrDesc,
  },
  {
    // CH + DE + NL. Its gate can only widen once the new market's CVD risk
    // region is wired into `SCORE2_REGION_BY_COUNTRY` (lib/score2.ts):
    // unlike every other calculator here, this model is recalibrated per
    // region, so a reader scored on another region's scales would be told a
    // materially different risk with nothing on the page looking wrong. That
    // was Germany's `moderate`, and it is the Netherlands' `low` — the same
    // region as Switzerland, so the two markets score identically and the
    // difference is which country the copy names.
    id: "score2-calculator",
    path: "/tools/score2-calculator",
    gate: "page:tools/score2-calculator",
    locales: ["de", "en", "fr", "nl"],
    titleKey: k.score2Title,
    descKey: k.score2Desc,
  },
  {
    // CH + DE, unlike the SCORE2 card above: FIB-4 has no per-country
    // calibration (lib/fib4.ts), so the German rollout was its copy in
    // messages/de-DE.json and nothing else. The filtering below reads the
    // gate, so this card now appears on the German /tools index and on the
    // German BMI and BMR pages too.
    id: "fib4-calculator",
    path: "/tools/fib4-calculator",
    gate: "page:tools/fib4-calculator",
    locales: ["de", "en", "fr", "nl"],
    titleKey: k.fib4Title,
    descKey: k.fib4Desc,
  },
  {
    // CH + DE, like the FIB-4 card above and for the same reason: sMAF-5's
    // coefficients carry no country term either (lib/smaf5.ts), so its
    // German rollout was its copy in messages/de-DE.json and nothing else.
    id: "smaf5-calculator",
    path: "/tools/smaf5-calculator",
    gate: "page:tools/smaf5-calculator",
    locales: ["de", "en", "fr", "nl"],
    titleKey: k.smaf5Title,
    descKey: k.smaf5Desc,
  },
  {
    // CH + DE, like the FIB-4 and sMAF-5 cards above: the MAFLD definition
    // has no per-country calibration (lib/mafld.ts), only a per-ETHNICITY
    // one, which does not change between markets. Its German rollout needed
    // one thing theirs did not, since it reads a lipid: the triglyceride
    // field asks that market in mg/dL (feature:tools-mgdl-lipids).
    id: "mafld-calculator",
    path: "/tools/mafld-calculator",
    gate: "page:tools/mafld-calculator",
    locales: ["de", "en", "fr", "nl"],
    titleKey: k.mafldTitle,
    descKey: k.mafldDesc,
  },
]

/**
 * The tools a visitor in `country` reading `locale` can actually open.
 *
 * Both filters matter and they answer different questions: the gate says
 * whether the market has the tool at all, `locales` says whether it has been
 * translated yet. A card that passes one but not the other is a 404.
 *
 * `tools` is injectable so tests can drive the filtering with more than the
 * registry happens to hold today.
 */
export function healthToolsFor(
  country: Country,
  locale: string,
  tools: readonly HealthTool[] = HEALTH_TOOLS
): readonly HealthTool[] {
  return tools.filter(
    (tool) =>
      isAllowedInCountry(tool.gate, country) && tool.locales.includes(locale)
  )
}

/**
 * Everything `healthToolsFor` would return, minus the page asking. Used by the
 * cross-link section on each tool page.
 */
export function otherHealthTools(
  currentId: string,
  country: Country,
  locale: string,
  tools: readonly HealthTool[] = HEALTH_TOOLS
): readonly HealthTool[] {
  return healthToolsFor(country, locale, tools).filter(
    (tool) => tool.id !== currentId
  )
}

/**
 * The locales in `country` that have at least one tool, i.e. the locales the
 * `/tools` index has anything to list. Derived rather than declared so the
 * index cannot outlive its own content: translate a calculator, widen its
 * `locales`, and the index locale appears with it.
 */
export function localesWithHealthTools(
  country: Country,
  candidates: readonly string[],
  tools: readonly HealthTool[] = HEALTH_TOOLS
): readonly string[] {
  return candidates.filter(
    (locale) => healthToolsFor(country, locale, tools).length > 0
  )
}
